import { atom, createStore } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import {
    DeviceDto,
    PlantDto,
    ProjectDto,
    SnackbarMessage,
    AreaDto,
    SystemDto,
    SuperiorTagDto,
    SortItem,
    StatusDto,
    SignalQuality,
    BatteryStatus,
    TimeseriesDto,
    SensorToEdit,
    TagDataDto,
    ExistingImageDto,
} from '../models/types'
import { Theme } from '@/state/settings'
import { Sensor } from '@/features/dashboard/models'
import { HubConnectionState } from '@microsoft/signalr'
import { SORT_OPTIONS } from '@/features/dashboard/components/dashboardTable/TableBar'

export interface SettingsState {
    theme: Theme
    isPortal: boolean
    isAuthenticated: boolean
}

const sessionStorageJotai = <Value>() => ({
    getItem: (key: string, initialValue: Value): Value => {
        const stored = sessionStorage.getItem(key)
        if (stored === null) return initialValue
        try {
            return JSON.parse(stored) as Value
        } catch {
            return initialValue
        }
    },
    setItem: (key: string, newValue: Value) => {
        sessionStorage.setItem(key, JSON.stringify(newValue))
    },
    removeItem: (key: string) => {
        sessionStorage.removeItem(key)
    },
})

export const errorMessagesAtom = atom<string[]>([])
export const snackbarMessageAtom = atom<SnackbarMessage | null>(null)
export const themeAtom = atom<Theme>(Theme.Comfortable)
export const isPortalAtom = atom<boolean>(false)
export const isAuthenticatedAtom = atom<boolean>(false)
export const selectedSensorCardAtom = atomWithStorage<SensorToEdit | null>(
    'sensor',
    null,
    sessionStorageJotai<SensorToEdit | null>()
)

export const selectedSensorToEditAtom = atom<Sensor | null>(null)
export const sensorAdditionalDataAtom = atom<TagDataDto[]>([])
export const selectedTimeSeriesAtom = atom<TimeseriesDto | null>(null)

export const selectedAmountAtom = atom<number>(0)

export const plantsAtom = atom<PlantDto[]>([])
export const devicesAtom = atom<DeviceDto[]>([])
export const projectsAtom = atom<ProjectDto[]>([])
export const areasAtom = atom<AreaDto[]>([])

export const selectedPlantAtom = atom<PlantDto | null>(null)
export const selectedDeviceAtom = atom<DeviceDto | null>(null)
export const selectedProtocolAtom = atom<string>('')
export const selectedProjectAtom = atom<ProjectDto | null>(null)
export const selectedAreaAtom = atom<AreaDto | null>(null)
export const showErrorSelectionBarAtom = atom<boolean>(false)
export const showErrorFormAtom = atom<boolean>(false)
export const errorFormVendorSerialNumberAtom = atom<string | null>(null)
export const showImageErrorsAtom = atom<boolean>(false)
export const showErrorOverviewAtom = atom<boolean>(false)

export const imagesAtom = atom<File[]>([])
export const descriptionsAtom = atom<string[]>([])

export const existingImagesAtom = atom<ExistingImageDto[]>([])

export const showErrorEditOnboardedAtom = atom<boolean>(false)
export const onboardedEditedAtom = atom<boolean>(false)

export const isOfflineAtom = atom<boolean>(false)

export const pageIndexDashboardAtom = atom<number>(1)

export const filterOpenAtom = atom<boolean>(false)
export const selectedFilterStatusAtom = atomWithStorage<StatusDto[]>('statuses', [], sessionStorageJotai<StatusDto[]>())
export const currentPlantAtom = atomWithStorage<PlantDto | null>('plant', null, sessionStorageJotai<PlantDto | null>())

export const currentPlantHydratedAtom = atom<boolean>(false)
export const selectedFilterDeviceAtom = atomWithStorage<DeviceDto | null>(
    'device',
    null,
    sessionStorageJotai<DeviceDto | null>()
)
export const selectedFilterSensorTypeAtom = atomWithStorage<string | null>(
    'sensortype',
    null,
    sessionStorageJotai<string | null>()
)
export const selectedFilterProtocolAtom = atomWithStorage<string>('protocol', '', sessionStorageJotai<string>())
export const selectedFilterProjectAtom = atomWithStorage<ProjectDto | null>(
    'project',
    null,
    sessionStorageJotai<ProjectDto | null>()
)
export const selectedFilterAreaAtom = atomWithStorage<AreaDto | null>(
    'area',
    null,
    sessionStorageJotai<AreaDto | null>()
)
export const selectedFilterSystemAtom = atomWithStorage<SystemDto | null>(
    'system',
    null,
    sessionStorageJotai<SystemDto | null>()
)
export const selectedFilterSuperiorTagAtom = atomWithStorage<SuperiorTagDto | null>(
    'superiortag',
    null,
    sessionStorageJotai<SuperiorTagDto | null>()
)
export const selectedFilterBatteryStatusAtom = atomWithStorage<BatteryStatus | null>(
    'batterystatus',
    null,
    sessionStorageJotai<BatteryStatus | null>()
)
export const selectedFilterSignalQualityAtom = atomWithStorage<SignalQuality | null>(
    'signalquality',
    null,
    sessionStorageJotai<SignalQuality | null>()
)

export const resetPlantFiltersAtom = atom(null, (_get, set) => {
    set(selectedFilterProjectAtom, null)
    set(selectedFilterSystemAtom, null)
    set(selectedFilterAreaAtom, null)
    set(selectedFilterSuperiorTagAtom, null)
})

export const resetDashboardFiltersAtom = atom(null, (_get, set) => {
    set(resetPlantFiltersAtom)
    set(selectedFilterStatusAtom, [])
    set(selectedFilterDeviceAtom, null)
    set(selectedFilterProtocolAtom, '')
    set(selectedFilterSensorTypeAtom, null)
    set(selectedFilterBatteryStatusAtom, null)
    set(selectedFilterSignalQualityAtom, null)
})

export const selectedFilterPortfolioSearchAtom = atomWithStorage<string>(
    'portfoliosearch',
    '',
    sessionStorageJotai<string>()
)
export const selectedFilterPortfolioMeasureTypeAtom = atomWithStorage<string | null>(
    'portfoliomeasuretype',
    null,
    sessionStorageJotai<string | null>()
)
export const selectedFilterPortfolioAtexAtom = atomWithStorage<string | null>(
    'portfolioatex',
    null,
    sessionStorageJotai<string | null>()
)
export const selectedFilterPortfolioProtocolAtom = atomWithStorage<string | null>(
    'portfolioprotocol',
    null,
    sessionStorageJotai<string | null>()
)
export const selectedFilterPortfolioIpRatingAtom = atomWithStorage<string | null>(
    'portfolioiprating',
    null,
    sessionStorageJotai<string | null>()
)
export const selectedFilterPortfolioVendorAtom = atomWithStorage<string | null>(
    'portfoliovendor',
    null,
    sessionStorageJotai<string | null>()
)

export const searchAtom = atom<string | null>(null)
export const isCardViewAtom = atomWithStorage<boolean>('iscardview', true, sessionStorageJotai<boolean>())

export const scannedSerialNumAtom = atom<any>(null)
export const scannedDataAtom = atom<any>(null)
export const activeStepAtom = atom<number>(0)

export const sortOpenAtom = atom<boolean>(false)
export const sortTagAtom = atom<SortItem>({ ...SORT_OPTIONS.tag, selected: null })
export const sortInstallationDateAtom = atom<SortItem>(SORT_OPTIONS.installationDate)
export const sortDeviceAtom = atom<SortItem>({ ...SORT_OPTIONS.device, selected: null })
export const sortSensorStatusAtom = atom<SortItem>({ ...SORT_OPTIONS.sensorStatus, selected: null })
export const sortBatteryCapacityAtom = atom<SortItem>({ ...SORT_OPTIONS.batteryCapacity, selected: null })
export const sortSignalStrengthAtom = atom<SortItem>({ ...SORT_OPTIONS.signalStrength, selected: null })

export const tableHeaderSortTagAtom = atom<string>(<'A-Z' | 'Z-A'>'A-Z')
export const tableHeaderSortDeviceAtom = atom<string>(<'A-Z' | 'Z-A'>'A-Z')

export const inputValuesAtom = atom<{
    SuperiorTag: string
    Area: string
    Coordinates: string
    Tag: string
    VendorSerialNumber: string
    ImageFiles: File[]
    ImageDescriptions: string[]
    UserAddedDescription: string
    CoordinatesNotAvailable: boolean
    SensorDbKey: string | null | undefined
    ExistingImages: ExistingImageDto[]
}>({
    SuperiorTag: '',
    Area: '',
    Coordinates: '',
    Tag: '',
    VendorSerialNumber: '',
    ImageFiles: [],
    ImageDescriptions: [],
    UserAddedDescription: '',
    CoordinatesNotAvailable: false,
    SensorDbKey: null,
    ExistingImages: [],
})

export const resetInputValuesAtom = atom(null, (_get, set) => {
    set(inputValuesAtom, {
        SuperiorTag: '',
        Area: '',
        Coordinates: '',
        Tag: '',
        VendorSerialNumber: '',
        ImageFiles: [],
        ImageDescriptions: [],
        UserAddedDescription: '',
        CoordinatesNotAvailable: false,
        SensorDbKey: null,
        ExistingImages: [],
    })
})

export const addSensorDataAtom = atom(null, (get, set, newSensor: Partial<Sensor>) => {
    const current = get(sensorDataAtom)
    set(sensorDataAtom, [...current, newSensor])
})

export const updateSensorDataAtom = atom(null, (_get, set, newList: Partial<Sensor>[]) => {
    set(sensorDataAtom, newList)
})

export const resetOnboardingAtom = atom(null, (get, set) => {
    const plant = get(selectedPlantAtom)
    const device = get(selectedDeviceAtom)
    const project = get(selectedProjectAtom)

    set(plantsAtom, [])
    set(devicesAtom, [])
    set(projectsAtom, [])
    set(scannedDataAtom, null)
    set(scannedSerialNumAtom, null)
    set(activeStepAtom, 0)
    set(inputValuesAtom, {
        SuperiorTag: '',
        Area: '',
        Coordinates: '',
        Tag: '',
        VendorSerialNumber: '',
        ImageFiles: [],
        ImageDescriptions: [],
        UserAddedDescription: '',
        CoordinatesNotAvailable: false,
        SensorDbKey: null,
        ExistingImages: [],
    })
    set(sensorDataAtom, [])

    set(selectedPlantAtom, plant)
    set(selectedDeviceAtom, device)
    set(selectedProjectAtom, project)
})

export const sensorDataAtom = atom<Partial<Sensor>[]>([])
export const globalStore = createStore()

export const connectionStatusAtom = atom<HubConnectionState>(HubConnectionState.Disconnected)
export const socketErrorMessagesAtom = atom<string[]>([])

globalStore.set(socketErrorMessagesAtom, [])

export function showOffline(offline: boolean): void {
    globalStore.set(isOfflineAtom, offline)
}

export function addSnackBarMessage(message: string): void {
    globalStore.set(snackbarMessageAtom, { content: message })
}

export function addSocketErrorMessage(message: string): void {
    globalStore.set(socketErrorMessagesAtom, (old) => (old.includes(message) ? old : old.concat(message)))
}

export function removeSocketErrorMessage(message: string): void {
    globalStore.set(socketErrorMessagesAtom, (old) => old.filter((val) => val !== message))
}

export function addGlobalErrorMessage(message: string): void {
    globalStore.set(errorMessagesAtom, (old) => old.concat(message))
}

export function removeGlobalErrorMessage(message: string): void {
    globalStore.set(errorMessagesAtom, (old) => old.filter((val) => val !== message))
}
